import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { isArrayLength } from '../../util/genericHelpers';
import { createImageVariantConfig } from '../../util/sdkLoader';
import {
  upsertUserFavorites,
  upsertListingLikes,
  saveWishlistAndRedirectToLogin,
} from '../../util/favoritesPageHelpers';
import { parse } from '../../util/urlHelpers';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { SharetribeApi } from '../../util/api';
import { showListing } from '../ListingPage/ListingPage.duck';
import { retrieveUserWishlist } from '../../util/dataExtractor';
const resultIds = data => data.data.map(l => l.id);

// Constants
const RESULT_PAGE_SIZE = 42;
const ACTION_TYPES = {
  FETCH_LISTING_REQUEST: 'app/FavoritesPage/FETCH_LISTING_REQUEST',
  FETCH_LISTING_SUCCESS: 'app/FavoritesPage/FETCH_LISTING_SUCCESS',
  FETCH_LISTING_ERROR: 'app/FavoritesPage/FETCH_LISTING_ERROR',
  ADD_TO_FAVORITES_REQUEST: 'app/FavoritesPage/ADD_TO_FAVORITES_REQUEST',
  ADD_TO_FAVORITES: 'app/FavoritesPage/ADD_TO_FAVORITES',
  REMOVE_FROM_FAVORITES: 'app/FavoritesPage/REMOVE_FROM_FAVORITES',
  REMOVE_ITEM_REQUEST: 'app/FavoritesPage/REMOVE accadeITEM_FROM_FAVORITES_REQUEST',
  REMOVE_ITEM_SUCCESS: 'app/FavoritesPage/REMOVE_ITEM_FROM_FAVORITES_SUCCESS',
  REMOVE_ITEM_ERROR: 'app/FavoritesPage/REMOVE_ITEM_FROM_FAVORITES_ERROR',
  UPDATE_CURRENT_PAGE_RESULT_IDS: 'app/FavoritesPage/UPDATE_CURRENT_PAGE_RESULT_IDS',
  UPDATE_FAVORITES_COUNT: 'app/FavoritesPage/UPDATE_FAVORITES_COUNT',
  CLEAN_FAVORITES_STATE: 'app/FavoritesPage/CLEAN_FAVORITES_STATE',
};

// Initial State
const initialState = {
  pagination: null,
  queryParams: null,
  favorites: [],
  favoritesCount: 0,
  fetchListingInProgress: false,
  fetchListingError: null,
  currentPageResultIds: [],
  removeItemInProgress: [],
  removeItemError: null,
  currentFavoriteId: null,
};

// Reducer
const favoritesPageReducer = (state = initialState, { type, payload } = {}) => {
  switch (type) {
    case ACTION_TYPES.CLEAN_FAVORITES_STATE:
      return { ...state, addToFavoritesInProgress: false, currentFavoriteId: null };

    case ACTION_TYPES.ADD_TO_FAVORITES_REQUEST:
      return { ...state, addToFavoritesInProgress: true, currentFavoriteId: payload };

    case ACTION_TYPES.ADD_TO_FAVORITES: {
      const updatedFavorites = Array.isArray(payload)
        ? payload
        : upsertUserFavorites({
            favorites: state.favorites,
            currentFavoriteId: null,
          });
      return {
        ...state,
        favorites: updatedFavorites,
        favoritesCount: updatedFavorites.length,
        addToFavoritesInProgress: false,
        currentFavoriteId: null,
      };
    }

    case ACTION_TYPES.REMOVE_FROM_FAVORITES: {
      const updatedFavorites = state.favorites.filter(item => item !== payload.id);
      return {
        ...state,
        favorites: updatedFavorites,
        favoritesCount: updatedFavorites.length,
        currentFavoriteId: null,
      };
    }

    case ACTION_TYPES.UPDATE_FAVORITES_COUNT:
      return { ...state, favoritesCount: payload };

    case ACTION_TYPES.FETCH_LISTING_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        fetchListingInProgress: true,
        currentPageResultIds: [],
        fetchListingError: null,
      };

    case ACTION_TYPES.FETCH_LISTING_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        fetchListingInProgress: false,
      };

    case ACTION_TYPES.UPDATE_CURRENT_PAGE_RESULT_IDS:
      return { ...state, currentPageResultIds: payload };

    case ACTION_TYPES.FETCH_LISTING_ERROR:
      return { ...state, fetchListingInProgress: false, fetchListingError: payload };

    case ACTION_TYPES.REMOVE_ITEM_REQUEST:
      return {
        ...state,
        removeItemInProgress: [...state.removeItemInProgress, payload],
        removeItemError: null,
        currentFavoriteId: payload,
      };

    case ACTION_TYPES.REMOVE_ITEM_SUCCESS:
      return {
        ...state,
        removeItemInProgress: state.removeItemInProgress.filter(id => id !== payload),
        removeItemError: null,
        currentFavoriteId: null,
      };

    case ACTION_TYPES.REMOVE_ITEM_ERROR:
      return {
        ...state,
        removeItemInProgress: [],
        removeItemError: payload,
        currentFavoriteId: null,
      };

    default:
      return state;
  }
};

export default favoritesPageReducer;

// Selectors
export const currentFavoriteIdSelector = state => state.FavoritesPage.currentFavoriteId;

// Action Creators
const fetchListingRequest = queryParams => ({
  type: ACTION_TYPES.FETCH_LISTING_REQUEST,
  payload: { queryParams },
});
const clearnFavoritesState = () => ({
  type: ACTION_TYPES.CLEAN_FAVORITES_STATE,
});

const fetchListingSuccess = response => ({
  type: ACTION_TYPES.FETCH_LISTING_SUCCESS,
  payload: { data: response.data },
});

const fetchListingError = error => ({
  type: ACTION_TYPES.FETCH_LISTING_ERROR,
  error: true,
  payload: error,
});

const addToFavoritesRequest = id => ({ type: ACTION_TYPES.ADD_TO_FAVORITES_REQUEST, payload: id });
export const addToFavorites = data => ({ type: ACTION_TYPES.ADD_TO_FAVORITES, payload: data });
const removeFromFavorites = id => ({ type: ACTION_TYPES.REMOVE_FROM_FAVORITES, payload: { id } });
const updateCurrentPageResultIds = ids => ({
  type: ACTION_TYPES.UPDATE_CURRENT_PAGE_RESULT_IDS,
  payload: ids,
});
const removeItemRequest = id => ({ type: ACTION_TYPES.REMOVE_ITEM_REQUEST, payload: id });
const removeItemSuccess = id => ({ type: ACTION_TYPES.REMOVE_ITEM_SUCCESS, payload: id });
const removeItemError = error => ({
  type: ACTION_TYPES.REMOVE_ITEM_ERROR,
  error: true,
  payload: error,
});

// Thunks
/** Adds an item to the user's favorites. */
export const addItemToFavorites = params => async dispatch => {
  const { config, currentFavoriteId, favorites = [], likes = [], isAuthenticated, userId } = params;
  dispatch(addToFavoritesRequest(currentFavoriteId));
  try {
    const updatedFavorites = upsertUserFavorites({ favorites, currentFavoriteId });
    const updatedLikes = upsertListingLikes({ likes, userId });

    if (isAuthenticated) {
      await dispatch(updateProfile({ publicData: { wishlist: updatedFavorites } }));
      await SharetribeApi.updateListing({
        id: currentFavoriteId,
        publicData: { likes: updatedLikes },
      });
      dispatch(addToFavorites(updatedFavorites));
      dispatch(showListing(currentFavoriteId, config));
    } else {
      saveWishlistAndRedirectToLogin(params);
      dispatch(clearnFavoritesState());
    }
    return updatedFavorites;
  } catch (error) {
    throw error;
  }
};

/** Removes an item from the user's favorites. */
export const deleteItemFromFavorites = params => async (dispatch, getState) => {
  const { id, isAuthenticated, favorites = [], likes = [], userId, config } = params;
  dispatch(removeItemRequest(id));
  try {
    const updatedFavorites = favorites.filter(item => item !== id);
    const updatedLikes = likes?.filter(likeId => likeId !== userId) || [];
    const updatedCurrentPageResultIds = getState().FavoritesPage.currentPageResultIds.filter(
      item => item?.uuid !== id
    );

    if (isAuthenticated && isArrayLength(favorites)) {
      await dispatch(updateProfile({ publicData: { wishlist: updatedFavorites } }));
      await SharetribeApi.updateListing({ id, publicData: { likes: updatedLikes } });
    }

    dispatch(addToFavorites(updatedFavorites));
    dispatch(updateCurrentPageResultIds(updatedCurrentPageResultIds));
    dispatch(showListing(id, config));
    dispatch(removeItemSuccess(id));
  } catch (error) {
    dispatch(removeItemError(storableError(error)));
  }
};

/** Fetches listings for the favorites page. */
export const fetchListings = (params, config) => async (dispatch, getState, sdk) => {
  dispatch(fetchListingRequest(params));
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  try {
    const queryParams = {
      ...params,
      include: ['author', 'images', 'currentStock'],
      'fields.listing': ['title', 'geolocation', 'price', 'description', 'publicData'],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    };

    const response = await sdk.listings.query(queryParams);
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchListingSuccess(response));
    return response;
  } catch (error) {
    dispatch(fetchListingError(storableError(error)));
    throw error;
  }
};

/** Loads initial data for the favorites page. */
export const loadData = (params, search, config) => async (dispatch, getState) => {
  try {
    const queryParams = parse(search);
    const page = queryParams.page || 1;
    const { currentUser } = getState().user || {};
    const favorites = retrieveUserWishlist(currentUser);

    if (isArrayLength(favorites)) {
      dispatch(addToFavorites(favorites));
      await dispatch(fetchListings({ ids: favorites, page, perPage: RESULT_PAGE_SIZE }, config));
    }
  } catch (error) {
    throw error;
  }
};
